/**
 * The one breakdown chart reports share: a count per bucket, drawn as bars in
 * the chart theme, where every bar is a drill (roadmap RP-7). Columns for a
 * handful of ordered buckets (status), bars for a list that can grow (segment
 * values, cycles). In a print view it draws at a fixed width and no bar is a
 * link.
 */

import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { chartTheme } from '../../../ds';
import { REPORT_PRINT_WIDTH_PX } from '../../../../pages/prioritization/reports/ReportPrintShell';

export interface BreakdownDatum {
  key: string | null;
  label: string;
  count: number;
  /** Concrete colour. */
  color: string;
  /** Where the bar leads. Absent means the bar is a number, not a link. */
  href?: string;
}

/** recharts hands a click either the datum itself or a wrapper carrying it as `payload`. */
function chartDatum<T extends object>(d: unknown): T | null {
  if (!d || typeof d !== 'object') return null;
  const wrapped = d as { payload?: T };
  if (wrapped.payload && typeof wrapped.payload === 'object') return wrapped.payload;
  return d as T;
}

export interface ReportBreakdownChartProps {
  data: ReadonlyArray<BreakdownDatum>;
  /** `columns`: buckets along the bottom. `bars`: buckets down the side, one row each. */
  orientation?: 'columns' | 'bars';
  /** What a count is a count of — the tooltip's noun. */
  valueLabel?: string;
  height?: number;
  print?: boolean;
}

/** The chart's own frame: responsive on screen, fixed width in print. */
export function ChartSurface({ height, print, children }: { height: number; print?: boolean; children: ReactElement }) {
  if (print) return <div style={{ width: REPORT_PRINT_WIDTH_PX - 48, height }}>{children}</div>;
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
    </div>
  );
}

export function ReportBreakdownChart({ data, orientation = 'columns', valueLabel = 'Items', height, print = false }: ReportBreakdownChartProps) {
  const navigate = useNavigate();
  const t = chartTheme();
  const bars = orientation === 'bars';
  const h = height ?? (bars ? Math.max(140, 28 * data.length + 40) : 220);
  const width = print ? REPORT_PRINT_WIDTH_PX - 48 : undefined;
  const clickable = !print && data.some((d) => d.href);

  const go = (d: unknown) => {
    const datum = chartDatum<BreakdownDatum>(d);
    if (datum?.href && !print) navigate(datum.href);
  };

  const chart = bars ? (
    <BarChart data={[...data]} layout="vertical" width={width} height={h} margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
      <CartesianGrid {...t.grid} horizontal={false} vertical />
      <XAxis type="number" allowDecimals={false} {...t.axis} />
      <YAxis type="category" dataKey="label" width={150} {...t.axis} />
      <Tooltip {...t.tooltip} formatter={(value: unknown) => [String(value), valueLabel]} />
      <Bar dataKey="count" onClick={go} cursor={clickable ? 'pointer' : undefined} isAnimationActive={false}>
        {data.map((e, i) => <Cell key={i} fill={e.color} />)}
      </Bar>
    </BarChart>
  ) : (
    <BarChart data={[...data]} width={width} height={h} barCategoryGap="30%" margin={{ left: 0, right: 8, top: 4, bottom: 4 }}>
      <CartesianGrid {...t.grid} />
      <XAxis dataKey="label" {...t.axis} />
      <YAxis allowDecimals={false} {...t.axis} />
      <Tooltip {...t.tooltip} formatter={(value: unknown) => [String(value), valueLabel]} />
      <Bar dataKey="count" onClick={go} cursor={clickable ? 'pointer' : undefined} isAnimationActive={false}>
        {data.map((e, i) => <Cell key={i} fill={e.color} />)}
      </Bar>
    </BarChart>
  );

  if (data.length === 0) {
    return <p className="py-6 text-center text-sm italic text-[var(--text-ter)]">Nothing to show in this range.</p>;
  }
  return <ChartSurface height={h} print={print}>{chart}</ChartSurface>;
}
